// 修復ブラシ / スポット修復 / コンテンツに応じた塗りつぶしの純ロジック。
// 「近くのパッチを写してポアソン合成で縫い合わせる」方式 (Photoshop の初期の修復と同じ考え方)。
// 大きな領域は粗い解像度から解く (マルチレゾリューション) ので反復回数が少なくて済む。

export interface Rgb {
  w: number
  h: number
  data: Uint8ClampedArray
}

/** mask (0..255) の非ゼロ範囲の矩形。無ければ null */
function maskBounds(mask: Uint8ClampedArray, w: number, h: number): { x: number; y: number; w: number; h: number } | null {
  let x0 = w, y0 = h, x1 = -1, y1 = -1
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (mask[y * w + x]! > 0) { if (x < x0) x0 = x; if (y < y0) y0 = y; if (x > x1) x1 = x; if (y > y1) y1 = y }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 }
}

function downsample(src: Float32Array, w: number, h: number, ch: number): { data: Float32Array; w: number; h: number } {
  const nw = Math.max(1, w >> 1), nh = Math.max(1, h >> 1)
  const out = new Float32Array(nw * nh * ch)
  for (let y = 0; y < nh; y++) for (let x = 0; x < nw; x++) {
    const x0 = Math.min(w - 1, x * 2), x1 = Math.min(w - 1, x * 2 + 1), y0 = Math.min(h - 1, y * 2), y1 = Math.min(h - 1, y * 2 + 1)
    for (let c = 0; c < ch; c++) out[(y * nw + x) * ch + c] = (src[(y0 * w + x0) * ch + c]! + src[(y0 * w + x1) * ch + c]! + src[(y1 * w + x0) * ch + c]! + src[(y1 * w + x1) * ch + c]!) / 4
  }
  return { data: out, w: nw, h: nh }
}

/** base の縮小: mask 内 (穴) の値は境界値を汚すので平均から除く */
function downsampleBase(src: Float32Array, M: Float32Array, w: number, h: number, ch: number): Float32Array {
  const nw = Math.max(1, w >> 1), nh = Math.max(1, h >> 1)
  const out = new Float32Array(nw * nh * ch)
  for (let y = 0; y < nh; y++) for (let x = 0; x < nw; x++) {
    const xs = [Math.min(w - 1, x * 2), Math.min(w - 1, x * 2 + 1)], ys = [Math.min(h - 1, y * 2), Math.min(h - 1, y * 2 + 1)]
    for (let c = 0; c < ch; c++) {
      let sum = 0, n = 0, all = 0
      for (const yy of ys) for (const xx of xs) {
        const i = yy * w + xx
        all += src[i * ch + c]!
        if (!M[i]) { sum += src[i * ch + c]!; n++ }
      }
      out[(y * nw + x) * ch + c] = n ? sum / n : all / 4
    }
  }
  return out
}

/**
 * ポアソン合成: mask 内の画素を、src の勾配を保ちつつ境界 (mask 外の base) に滑らかに接続する。
 * base と src は同じサイズの RGBA。戻り値は新しい RGBA (mask 外は base のまま)。
 */
export function poissonBlend(base: Uint8ClampedArray, src: Uint8ClampedArray, mask: Uint8ClampedArray, w: number, h: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(base)
  const b = maskBounds(mask, w, h)
  if (!b) return out
  // 作業領域: 境界を 8px 含める (粗いレベルでも境界画素が残るように)
  const PAD = 8
  const x0 = Math.max(0, b.x - PAD), y0 = Math.max(0, b.y - PAD)
  const x1 = Math.min(w - 1, b.x + b.w - 1 + PAD), y1 = Math.min(h - 1, b.y + b.h - 1 + PAD)
  const rw = x1 - x0 + 1, rh = y1 - y0 + 1
  const CH = 3
  const B = new Float32Array(rw * rh * CH), S = new Float32Array(rw * rh * CH), M = new Float32Array(rw * rh)
  for (let y = 0; y < rh; y++) for (let x = 0; x < rw; x++) {
    const gi = (y0 + y) * w + (x0 + x), li = y * rw + x
    M[li] = mask[gi]! > 32 ? 1 : 0
    for (let c = 0; c < CH; c++) { B[li * CH + c] = base[gi * 4 + c]!; S[li * CH + c] = src[gi * 4 + c]! }
  }
  // ピラミッド
  const levels: { B: Float32Array; S: Float32Array; M: Float32Array; w: number; h: number }[] = [{ B, S, M, w: rw, h: rh }]
  while (levels[levels.length - 1]!.w > 4 && levels[levels.length - 1]!.h > 4) {
    const top = levels[levels.length - 1]!
    const nb = { data: downsampleBase(top.B, top.M, top.w, top.h, CH) }, ns = downsample(top.S, top.w, top.h, CH), nm = downsample(top.M, top.w, top.h, 1)
    // 4 画素すべてが穴のときだけ穴にする (境界側に寄せて、粗いレベルでも境界条件が残る)
    for (let i = 0; i < nm.data.length; i++) nm.data[i] = nm.data[i]! >= 0.999 ? 1 : 0
    levels.push({ B: nb.data, S: ns.data, M: nm.data, w: ns.w, h: ns.h })
  }
  let guess: Float32Array | null = null
  for (let li = levels.length - 1; li >= 0; li--) {
    const L = levels[li]!
    const { w: lw, h: lh } = L
    const X = new Float32Array(L.B)
    if (guess) {
      // 1 段粗い解を拡大して初期値に (mask 内だけ)
      const g = levels[li + 1]!
      for (let y = 0; y < lh; y++) for (let x = 0; x < lw; x++) {
        const i = y * lw + x
        if (!L.M[i]) continue
        const gx = Math.min(g.w - 1, x >> 1), gy = Math.min(g.h - 1, y >> 1)
        for (let c = 0; c < CH; c++) X[i * CH + c] = guess[(gy * g.w + gx) * CH + c]!
      }
    }
    const iters = li === 0 ? 50 : 30
    const lap = new Float32Array(lw * lh * CH)
    for (let y = 0; y < lh; y++) for (let x = 0; x < lw; x++) {
      const i = y * lw + x
      if (!L.M[i]) continue
      const xl = x > 0 ? i - 1 : i, xr = x < lw - 1 ? i + 1 : i, yu = y > 0 ? i - lw : i, yd = y < lh - 1 ? i + lw : i
      for (let c = 0; c < CH; c++) lap[i * CH + c] = 4 * L.S[i * CH + c]! - L.S[xl * CH + c]! - L.S[xr * CH + c]! - L.S[yu * CH + c]! - L.S[yd * CH + c]!
    }
    for (let it = 0; it < iters; it++) {
      // Gauss-Seidel (赤黒順序なしの単純掃引)
      for (let y = 0; y < lh; y++) for (let x = 0; x < lw; x++) {
        const i = y * lw + x
        if (!L.M[i]) continue
        const xl = x > 0 ? i - 1 : i, xr = x < lw - 1 ? i + 1 : i, yu = y > 0 ? i - lw : i, yd = y < lh - 1 ? i + lw : i
        for (let c = 0; c < CH; c++) X[i * CH + c] = (X[xl * CH + c]! + X[xr * CH + c]! + X[yu * CH + c]! + X[yd * CH + c]! + lap[i * CH + c]!) / 4
      }
    }
    guess = X
  }
  const X = guess!
  for (let y = 0; y < rh; y++) for (let x = 0; x < rw; x++) {
    const li = y * rw + x
    const gi = (y0 + y) * w + (x0 + x)
    // ブラシのカバレッジは流量で 255 未満になりやすいので、96 以上は完全に置き換える
    const m = Math.min(1, mask[gi]! / 96)
    if (m <= 0) continue
    for (let c = 0; c < CH; c++) out[gi * 4 + c] = base[gi * 4 + c]! * (1 - m) + Math.max(0, Math.min(255, X[li * CH + c]!)) * m
    out[gi * 4 + 3] = Math.max(base[gi * 4 + 3]!, Math.round(src[gi * 4 + 3]! * m))
  }
  return out
}

/**
 * 領域 (mask) を埋めるのに良いパッチのオフセットを探す。
 * 候補は領域の周囲 (半径の 1.2〜3 倍) の円周上。シフトした領域が画像内かつ mask と重ならず、
 * 境界帯の色差 (SSD) が最小のものを選ぶ。
 */
export function findPatchOffset(img: Uint8ClampedArray, w: number, h: number, mask: Uint8ClampedArray, bounds?: { x: number; y: number; w: number; h: number } | null, avoid: Uint8ClampedArray = mask): { dx: number; dy: number } | null {
  const b = bounds ?? maskBounds(mask, w, h)
  if (!b) return null
  // 境界帯: mask の外側 3px の画素
  const band: number[] = []
  for (let y = Math.max(0, b.y - 3); y <= Math.min(h - 1, b.y + b.h + 2); y++) for (let x = Math.max(0, b.x - 3); x <= Math.min(w - 1, b.x + b.w + 2); x++) {
    const i = y * w + x
    if (mask[i]! > 0 || avoid[i]! > 0) continue
    // 3px 以内に mask があるか
    let near = false
    for (let dy = -3; dy <= 3 && !near; dy++) for (let dx = -3; dx <= 3; dx++) {
      const xx = x + dx, yy = y + dy
      if (xx >= 0 && yy >= 0 && xx < w && yy < h && mask[yy * w + xx]! > 0) { near = true; break }
    }
    if (near) band.push(i)
  }
  if (!band.length) return null
  // 領域内の画素 (重なり判定用、間引き)
  const inside: number[] = []
  for (let y = b.y; y < b.y + b.h; y += 2) for (let x = b.x; x < b.x + b.w; x += 2) if (mask[y * w + x]! > 0) inside.push(y * w + x)
  let best: { dx: number; dy: number; cost: number } | null = null
  const dirs = 24
  // 最小距離 = 領域の長辺 + 2 (軸方向に動かせば重ならない)。そこから数段階外側まで探す
  const d0 = Math.max(b.w, b.h) + 2
  for (const ring of [1, 1.5, 2, 3, 4]) {
    const dist = d0 * ring
    if (dist > Math.max(w, h)) break
    for (let k = 0; k < dirs; k++) {
      const a = (k / dirs) * Math.PI * 2
      const dx = Math.round(Math.cos(a) * dist), dy = Math.round(Math.sin(a) * dist)
      // 画像内・非重なり
      if (b.x + dx < 0 || b.y + dy < 0 || b.x + b.w + dx > w || b.y + b.h + dy > h) continue
      let ok = true
      for (const i of inside) {
        const x = i % w, y = (i - x) / w
        if (avoid[(y + dy) * w + (x + dx)]! > 0) { ok = false; break }
      }
      if (!ok) continue
      let cost = 0
      for (const i of band) {
        const x = i % w, y = (i - x) / w
        const xx = x + dx, yy = y + dy
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) { cost += 3 * 255 * 255; continue }
        const j = (yy * w + xx) * 4, o = i * 4
        if (img[j + 3]! < 8) { cost += 3 * 255 * 255; continue }
        const dr = img[o]! - img[j]!, dg = img[o + 1]! - img[j + 1]!, db = img[o + 2]! - img[j + 2]!
        cost += dr * dr + dg * dg + db * db
      }
      if (!best || cost < best.cost) best = { dx, dy, cost }
    }
  }
  return best ? { dx: best.dx, dy: best.dy } : null
}

/**
 * img を (dx,dy) だけずらしたソース画像を作る。ポアソン合成は mask の境界の外側 1px も
 * ソースの勾配として読むので、mask を 2px 広げた範囲まで写す (継ぎ目の段差を勾配に含めない)。
 */
function shifted(img: Uint8ClampedArray, w: number, h: number, mask: Uint8ClampedArray, dx: number, dy: number, into?: Uint8ClampedArray): Uint8ClampedArray {
  const out = into ?? new Uint8ClampedArray(img)
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x
    let near = mask[i]! > 0
    for (let ddy = -2; ddy <= 2 && !near; ddy++) for (let ddx = -2; ddx <= 2; ddx++) {
      const xx = x + ddx, yy = y + ddy
      if (xx >= 0 && yy >= 0 && xx < w && yy < h && mask[yy * w + xx]! > 0) { near = true; break }
    }
    if (!near) continue
    const sx = Math.max(0, Math.min(w - 1, x + dx)), sy = Math.max(0, Math.min(h - 1, y + dy))
    const j = (sy * w + sx) * 4
    out[i * 4] = img[j]!; out[i * 4 + 1] = img[j + 1]!; out[i * 4 + 2] = img[j + 2]!; out[i * 4 + 3] = img[j + 3]!
  }
  return out
}

/** スポット修復: 近くのパッチを探して写し、ポアソン合成 */
export function spotHeal(img: Uint8ClampedArray, w: number, h: number, mask: Uint8ClampedArray): Uint8ClampedArray {
  const off = findPatchOffset(img, w, h, mask)
  if (!off) return new Uint8ClampedArray(img)
  return poissonBlend(img, shifted(img, w, h, mask, off.dx, off.dy), mask, w, h)
}

/** 修復ブラシ: すでに写した src (コピースタンプの結果) を境界に馴染ませる */
export function healBlend(original: Uint8ClampedArray, cloned: Uint8ClampedArray, mask: Uint8ClampedArray, w: number, h: number): Uint8ClampedArray {
  return poissonBlend(original, cloned, mask, w, h)
}

/**
 * コンテンツに応じた塗りつぶし: 領域をブロックに分け、ブロックごとに近くのパッチを探して写し、
 * 全体をポアソン合成する (単一パッチより繰り返し感が少ない)。
 */
export function contentAwareFill(img: Uint8ClampedArray, w: number, h: number, mask: Uint8ClampedArray, block = 16): Uint8ClampedArray {
  const b = maskBounds(mask, w, h)
  if (!b) return new Uint8ClampedArray(img)
  const src = new Uint8ClampedArray(img)
  const sub = new Uint8ClampedArray(w * h)
  for (let by = b.y; by < b.y + b.h; by += block) {
    for (let bx = b.x; bx < b.x + b.w; bx += block) {
      sub.fill(0)
      let any = false
      const bw = Math.min(block, b.x + b.w - bx), bh = Math.min(block, b.y + b.h - by)
      for (let y = by; y < by + bh; y++) for (let x = bx; x < bx + bw; x++) if (mask[y * w + x]! > 0) { sub[y * w + x] = 255; any = true }
      if (!any) continue
      const off = findPatchOffset(img, w, h, sub, { x: bx, y: by, w: bw, h: bh }, mask) ?? findPatchOffset(img, w, h, mask, b)
      if (!off) continue
      shifted(img, w, h, sub, off.dx, off.dy, src)
    }
  }
  return poissonBlend(img, src, mask, w, h)
}
